import {useEffect, useState} from "react";
import {connect} from "react-redux";
import {EventHandler, EventListener} from "../EventDispatcher";
import {IpcMainInvokeEvent, IpcRendererEvent} from "electron";
import DocumentDownload from "../../Entity/DocumentDownload";
import {DownloadState} from "../../Reducer/Download";
import getEventDispatcher from "../../DependencyInjection/eventDispatcher";
import {
    addCurrentDownloadAction,
    addDownload,
    cancelCurrentDownload,
    cancelDownload,
    changeProgressDownload,
    finishDownload,
    moveDownDownload,
    moveUpDownload,
    pauseDownload,
    processDownload,
    resumeDownload
} from "../../Action/Download";
import Progress from "../../ValueObject/Progress";
import CreateResourceListener from "../Listener/CreateResourceListener";
import DownloadListener from "../Listener/DownloadListener";
import DownloadProgressListener from "../Listener/DownloadProgressListener";
import LoadPendingDownloadListener from "../Listener/LoadPendingDownloadListener";
import {
    ChangeDocumentStatusToDownloadedListener,
    ChangeDocumentStatusToDownloadingListener,
    ChangeDocumentStatusToPausedListener,
    ChangeDocumentStatusToProcessingListener
} from "../Listener/ChangeDocumentStatusListeners";
import SaveDownloadFileListener from "../Listener/SaveDownloadFileListener";
import CancelDownloadListener from "../Listener/CancelDownloadListener";
import PauseDownloadListener from "../Listener/PauseDownloadListener";
import ResumeDownloadListener from "../Listener/ResumeDownloadListener";
import MoveUpDownloadListener from "../Listener/MoveUpDownloadListener";
import ChangeDocumentPositionsListener from "../Listener/ChangeDocumentPositionsListener";
import MoveDownDownloadListener from "../Listener/MoveDownDownloadListener";
import CancelCurrentDownloadListener from "../Listener/CancelCurrentDownloadListener";
import PartialDownloadListener from "../Listener/PartialDownloadListener";
import PartialDownloadProgressListener from "../Listener/PartialDownloadProgressListener";

type DownloadEventHandlerProps = {
    addDownload: (document: DocumentDownload) => void;
    addCurrentDownloadAction: () => void;
    pauseDownload: () => void;
    resumeDownload: () => void;
    cancelDownload: (id: number) => void;
    cancelCurrentDownload: () => void;
    processDownload: () => void;
    finishDownload: () => void;
    moveUpDownload: (id: number) => void;
    moveDownDownload: (id: number) => void;
    changeProgressDownload: (progress: Progress) => void;
}

const eventDispatcher = getEventDispatcher();

const DownloadEventHandler = (props: mapStateToPropsType & DownloadEventHandlerProps): null => {
    const {
        token,
        isNetworkAvailable,
        currentDownload,
        closeDownload,
        documents,
        progress
    } = props;

    const AddDownloadAction: EventHandler<DocumentDownload> = async (event: IpcMainInvokeEvent, content: DocumentDownload): Promise<void> => {
        props.addDownload(DocumentDownload.from(content));
    }

    const AddCurrentDownloadAction: EventHandler<null> = async (event: IpcMainInvokeEvent, content: null): Promise<void> => {
        props.addCurrentDownloadAction();
    }

    const PauseDownloadAction: EventHandler<null> = async (event: IpcMainInvokeEvent, content: null): Promise<void> => {
        props.pauseDownload();
    }

    const ResumeDownloadAction: EventHandler<null> = async (event: IpcMainInvokeEvent, content: null): Promise<void> => {
        props.resumeDownload();
    }

    const CancelDownloadAction: EventHandler<number> = async (event: IpcMainInvokeEvent, content: number): Promise<void> => {
        props.cancelDownload(content);
    }

    const CancelCurrentDownloadAction: EventHandler<null> = async (event: IpcMainInvokeEvent, content: null): Promise<void> => {
        props.cancelCurrentDownload();
    }

    const ProcessDownloadAction: EventHandler<null> = async (event: IpcMainInvokeEvent, content: null): Promise<void> => {
        props.processDownload();
    }

    const FinishDownloadAction: EventHandler<null> = async (event: IpcMainInvokeEvent, content: null): Promise<void> => {
        props.finishDownload();
    }

    const MoveUpDownloadAction: EventHandler<number> = async (event: IpcMainInvokeEvent, content: number): Promise<void> => {
        props.moveUpDownload(content);
    }

    const MoveDownDownloadAction: EventHandler<number> = async (event: IpcMainInvokeEvent, content: number): Promise<void> => {
        props.moveDownDownload(content);
    }

    const ChangeProgressDownloadAction: EventHandler<Progress> = async (event: IpcMainInvokeEvent, content: Progress): Promise<void> => {
        props.changeProgressDownload(Progress.from(content));
    }

    const CreateResourceFromData: EventListener<any> = async (event: IpcRendererEvent, content: any): Promise<void> => {
        await eventDispatcher.dispatch('CreateResource', content);
    }

    const Listeners: Map<string, EventListener<any>> = new Map(
        [
            ['CreateResourceFromData', CreateResourceFromData],
        ] as readonly [string, EventListener<any>][]
    );

    const Handlers: Map<string, EventHandler<any>> = new Map(
        [
            ['CreateResource', CreateResourceListener],
            ['Download', DownloadListener],
            ['PartialDownload', PartialDownloadListener],
            ['DownloadProgress', DownloadProgressListener],
            ['PartialDownloadProgress', PartialDownloadProgressListener],
            ['LoadPendingDownload', LoadPendingDownloadListener],
            ['ChangeDocumentStatusToDownloading', ChangeDocumentStatusToDownloadingListener],
            ['ChangeDocumentStatusToPaused', ChangeDocumentStatusToPausedListener],
            ['ChangeDocumentStatusToProcessing', ChangeDocumentStatusToProcessingListener],
            ['ChangeDocumentStatusToDownloaded', ChangeDocumentStatusToDownloadedListener],
            ['ChangeDocumentPositions', ChangeDocumentPositionsListener],
            ['PauseDownload', PauseDownloadListener],
            ['ResumeDownload', ResumeDownloadListener],
            ['MoveUpDownload', MoveUpDownloadListener],
            ['MoveDownDownload', MoveDownDownloadListener],
            ['CancelDownload', CancelDownloadListener],
            ['CancelCurrentDownload', CancelCurrentDownloadListener],
            ['SaveDownloadFile', SaveDownloadFileListener],
            ['AddDownloadAction', AddDownloadAction],
            ['AddCurrentDownloadAction', AddCurrentDownloadAction],
            ['PauseDownloadAction', PauseDownloadAction],
            ['ResumeDownloadAction', ResumeDownloadAction],
            ['CancelDownloadAction', CancelDownloadAction],
            ['CancelCurrentDownloadAction', CancelCurrentDownloadAction],
            ['ProcessDownloadAction', ProcessDownloadAction],
            ['FinishDownloadAction', FinishDownloadAction],
            ['MoveUpDownloadAction', MoveUpDownloadAction],
            ['MoveDownDownloadAction', MoveDownDownloadAction],
            ['ChangeProgressDownloadAction', ChangeProgressDownloadAction],
        ] as readonly [string, EventHandler<any>][]
    );

    const [getChangeDownloadPositions, setChangeDownloadPositions] = useState<boolean>(false);

    const ChangeDownloadPositionsAction: EventHandler<null> = async (event: IpcMainInvokeEvent, content: null): Promise<void> => {
        setChangeDownloadPositions(true);
    }

    const FinishCurrentDownloadAction: EventHandler<DocumentDownload> = async (event: IpcMainInvokeEvent, content: DocumentDownload): Promise<void> => {
        const documentDownload: DocumentDownload = DocumentDownload.from(content);

        await eventDispatcher.dispatch('ChangeDocumentStatusToProcessing', documentDownload);
        await eventDispatcher.dispatch('ProcessDownloadAction');
    }

    const FinishCurrentDownloadWithErrorAction: EventHandler<DocumentDownload> = async (event: IpcMainInvokeEvent, content: DocumentDownload): Promise<void> => {
        const documentDownload: DocumentDownload = DocumentDownload.from(content);

        Promise.all([
            eventDispatcher.dispatch('CancelCurrentDownload', documentDownload)
        ]);
    }

    Handlers.set('ChangeDownloadPositionsAction', ChangeDownloadPositionsAction);
    Handlers.set('FinishCurrentDownloadAction', FinishCurrentDownloadAction);
    Handlers.set('FinishCurrentDownloadWithErrorAction', FinishCurrentDownloadWithErrorAction);

    const SetListenersReactive = () => {
        for (const [event, eventListener] of Array.from(Listeners)) {
            eventDispatcher.addListener(event, eventListener);
        }

        for (const [event, eventHandler] of Array.from(Handlers)) {
            eventDispatcher.addHandler(event, eventHandler);
        }

        return (): void => {
            for (const [event, eventListener] of Array.from(Listeners)) {
                eventDispatcher.removeListener(event, eventListener);
            }

            for (const [event] of Array.from(Handlers)) {
                eventDispatcher.removeHandler(event);
            }
        };
    }

    const LoadPendingDownloadReactive = (): void => {
        if (isNetworkAvailable && token) {
            eventDispatcher.dispatch('LoadPendingDownload', token);
        }
    }

    const ChangeDownloadPositionsReactive = (): void => {
        setChangeDownloadPositions(false);

        if (documents.length > 0) {
            eventDispatcher.dispatch('ChangeDocumentPositions', documents);
        }
    }

    const LoadFirstDownloadReactive = (): void => {
        if (null === currentDownload || !currentDownload.isPendingStatus()) {
            return;
        }

        Promise.all([
            eventDispatcher.dispatch('ChangeDocumentStatusToDownloading', currentDownload),
            eventDispatcher.dispatch('ResumeDownloadAction'),
            eventDispatcher.dispatch('Download', currentDownload)
        ]);
    }

    const EndFirstDownloadReactive = (): void => {
        if (null === closeDownload || !closeDownload.isProcessingStatus()) {
            return;
        }

        Promise.all([
            eventDispatcher.dispatch('SaveDownloadFile', closeDownload),
            eventDispatcher.dispatch('FinishDownloadAction'),
            eventDispatcher.dispatch('AddCurrentDownloadAction')
        ]);
    }

    const DocumentsReactive = (): void => {
        if (getChangeDownloadPositions) {
            ChangeDownloadPositionsReactive();
        }
    }

    useEffect(SetListenersReactive, []);
    useEffect(LoadPendingDownloadReactive, [isNetworkAvailable, token]);
    useEffect(LoadFirstDownloadReactive, [currentDownload]);
    useEffect(EndFirstDownloadReactive, [closeDownload]);
    useEffect(DocumentsReactive, [documents.length, progress]);

    return null;
}

type DownloadEventHandlerStageProps = {
    download: DownloadState,
    session: {
        token: string | null;
    };
    sensors: {
        isNetworkAvailable: boolean;
    }
}

type mapStateToPropsType = {
    documents: DocumentDownload[];
    currentDownload: DocumentDownload | null;
    closeDownload: DocumentDownload | null;
    progress: Progress,
    token: string | null;
    isNetworkAvailable: boolean;
}

const mapStateToProps = (state: DownloadEventHandlerStageProps): mapStateToPropsType => ({
    documents: state.download.download,
    currentDownload: state.download.currentDownload,
    closeDownload: state.download.closeDownload,
    progress: state.download.progress,
    token: state.session.token,
    isNetworkAvailable: state.sensors.isNetworkAvailable,
});

export default connect(mapStateToProps, {
    addDownload,
    addCurrentDownloadAction,
    cancelDownload,
    cancelCurrentDownload,
    changeProgressDownload,
    processDownload,
    finishDownload,
    moveDownDownload,
    moveUpDownload,
    pauseDownload,
    resumeDownload
})(DownloadEventHandler);